/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Download } from '@carbon/react/icons';
import {
  Button,
  ListItem,
  OrderedList,
  PasswordInput,
  Toggle,
  UnorderedList
} from 'carbon-components-react';
import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { endpoints } from '@ibm-watson/shared/modules/ia/util/config';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { LiveAgentTab as tabMessages } from '../../web-chat/messages';
import { FIELDS as WEBCHAT_FIELDS } from '../../web-chat/schema';
import { links } from '../links';
import { Zendesk as messages } from '../messages';
import { FIELDS } from './schema';

const IDS = {
  SHARED_SECRET: 'zendesk_shared_secret'
};

interface ZendeskPrivateAppProps {
  /**
   *
   */
  formMethods: any;
  /**
   *
   */
  webChatFormMethods: any;
  /**
   *
   */
  readOnly?: boolean;
}
export const PrivateApp = ({
  formMethods,
  webChatFormMethods,
  readOnly
}: ZendeskPrivateAppProps) => {
  const { formatMessage: t } = useIntl();
  const { control } = formMethods;
  const { getFieldValue, registerInputField } = useFormHelpers(formMethods);

  const webChatFormHelpers = useFormHelpers(webChatFormMethods);

  const securityEnabled = webChatFormHelpers.getFieldValue(
    WEBCHAT_FIELDS.AUTHORIZATION.IS_ON
  );

  const clientTokenChangeState = useState<boolean>(false);

  const enableAuthentication = getFieldValue(FIELDS.AUTHENTICATION_ENABLED);
  const sharedSecret = getFieldValue(FIELDS.CLIENT_PRIVATE_TOKEN);

  const getAgentApplicationURL = () =>
    `${
      endpoints.webChat
    }/serviceDesks/watson-assistant-zendesk-servicedesk.zip?cb=${Date.now()}`;

  const transferUrl = useIsNewIA()
    ? links.learnMoreTransferSupportNewIA
    : links.learnMoreTransferSupportClassic;

  const authenticateUrl = useIsNewIA()
    ? links.learnMoreZendeskSecureNewIA
    : links.learnMoreZendeskSecureClassic;

  const chatHistoryUrl = useIsNewIA()
    ? links.learnMoreZendeskPrivateAppNewIA
    : links.learnMoreZendeskPrivateAppClassic;

  return (
    <div>
      <div className="TabContent__field-container">
        <h4>
          <FormattedMessage {...messages.chatHistoryTitle} />
        </h4>
        <div className="TabContent__spacer8"></div>
        <div className="TabContent__subtitle">
          <FormattedMessageWithLink
            url={chatHistoryUrl}
            {...messages.chatHistoryDescription}
          />
        </div>
        <OrderedList className="TabContent__ordered-list">
          <ListItem className="ServiceDesk__download-instruction">
            <FormattedMessage {...messages.chatHistoryStep1} />
            {/*Need to disable this button but connectionStatus !== LoaderStates.SUCCESS does not work*/}
            <Button
              className="ServiceDesk__download-button"
              id="zendesk__download-private-app"
              hasIconOnly={true}
              kind="ghost"
              size="sm"
              disabled={readOnly}
              aria-label={t(messages.downloadApp)}
              iconDescription={t(messages.downloadApp)}
              onClick={() =>
                window.open(`${getAgentApplicationURL()}`, '_blank')
              }>
              <Download size={16} />
            </Button>
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.chatHistoryStep2} />
          </ListItem>
        </OrderedList>
      </div>
      <div className="ServiceDesk__divider"></div>
      <div className="TabContent__spacer16"></div>
      <div className="TabContent__field-container">
        <h4>
          <FormattedMessage {...messages.authenticateUsersTitle} />
        </h4>
        <div className="TabContent__spacer8"></div>
        <div className="TabContent__subtitle">
          <FormattedMessageWithLink
            url={authenticateUrl}
            {...messages.authenticateUsersDescription1}
          />
        </div>
        <div className="TabContent__subtitle">
          <FormattedMessage {...messages.authenticateUsersDescription2} />
        </div>
        <UnorderedList className="TabContent__unordered-list">
          <ListItem>
            <FormattedMessage {...messages.authenticateUsersStep1} />
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.authenticateUsersStep2} />
          </ListItem>
        </UnorderedList>
        <Controller
          control={control}
          name={FIELDS.AUTHENTICATION_ENABLED}
          render={({ field: { onChange, value } }) => (
            <Toggle
              className="TabContent__inner-field"
              disabled={readOnly || !securityEnabled}
              id={FIELDS.AUTHENTICATION_ENABLED}
              onToggle={onChange}
              toggled={value}
              size="sm"
            />
          )}
        />
      </div>
      {enableAuthentication && securityEnabled && (
        <>
          <div className="TabContent__field-container">
            <div className="TabContent__subtitle">
              <FormattedMessage
                {...messages.authenticateUsersToggleDescription}
              />
            </div>
            <h5 className="TabContent__inner-title TabContent__inner-title-heading">
              <FormattedMessage {...messages.sharedSecretTitle} />
            </h5>
            <div className="TabContent__spacer16"></div>
            <PasswordInput
              {...registerInputField({
                field: FIELDS.CLIENT_PRIVATE_TOKEN,
                isPasswordField: true,
                labelText: messages.sharedSecretLabel,
                placeholderText: messages.sharedSecretPlaceholder,
                changeState: clientTokenChangeState,
                readOnly
              })}
              id={IDS.SHARED_SECRET}
              className="TabContent__field"
            />
          </div>
          <div className="ServiceDesk__divider"></div>
          <div className="TabContent__spacer16"></div>
          <div className="TabContent__field-container">
            <h4>
              <FormattedMessage {...messages.unidentifiedUsersTitle} />
            </h4>
            <div className="TabContent__spacer8"></div>
            <div className="TabContent__subtitle">
              <FormattedMessageWithLink
                url={links.learnMoreZendeskSecureAnonymous}
                {...messages.unidentifiedUsersDescription}
              />
            </div>

            <Controller
              control={control}
              name={FIELDS.AUTHENTICATE_ANONYMOUS_USERS}
              render={({ field: { onChange, value } }) => (
                <Toggle
                  disabled={readOnly || sharedSecret === ''}
                  className="TabContent__inner-field"
                  labelText={t(messages.unidentifiedUsersLabel)}
                  id={FIELDS.AUTHENTICATE_ANONYMOUS_USERS}
                  onToggle={onChange}
                  toggled={value}
                  size="sm"
                />
              )}
            />
          </div>
        </>
      )}
      <div className="ServiceDesk__divider"></div>
      <div className="TabContent__spacer16"></div>
      <div className="TabContent__field-container">
        <h4>
          <FormattedMessage {...tabMessages.transferSupportTitle} />
        </h4>
        <div className="TabContent__spacer8"></div>
        <div className="TabContent__subtitle">
          <FormattedMessageWithLink
            url={transferUrl}
            {...tabMessages.transferSupportDescription}
          />
        </div>
      </div>
    </div>
  );
};
